// MUST STAY FIRST. Side-effecting import: it tells Capgo the freshly-applied
// OTA bundle booted, before any other module gets a chance to throw and strand
// the signal. See src/lib/ota-ready.ts for why that ordering is the fix.
import '@/lib/ota-ready'
import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
// Self-host Inter weights used across the app — avoids exposing visitor IPs
// to Google via fonts.googleapis.com. Weights match the previous Google Fonts
// query (400/500/600/700).
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import './index.css'
import './styles/tour-animations.css'
import { initErrorMonitor } from '@/lib/error-monitor'
import { initPerfMonitor } from '@/lib/perf-monitor'
import { installChunkReloadGuard, installStaleBuildWatch } from '@/lib/chunk-reload'
import { validateEnvironment } from '@/lib/env-validation'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { SplashScreen } from '@capacitor/splash-screen'
import { initOtaUpdates } from '@/lib/ota-update-manager'
import { OtaUpdateBanner } from '@/components/ota/OtaUpdateBanner'

// Validate environment variables first (will throw if invalid)
validateEnvironment();

// Initialize the in-house error monitor (replaces Sentry; posts to the
// log-client-error Supabase function, aggregated by the error-digest
// routine).
initErrorMonitor();
// Load-time sampling. Same pipeline as errors — see src/lib/perf-sample.ts
// for why this exists and what it deliberately does not record.
initPerfMonitor();

// Recover from stale hashed chunks after a deploy: when a still-open tab
// clicks a lazily-imported feature whose asset URL no longer exists, reload
// once to pick up the fresh manifest instead of dead-ending on "please try
// again". See src/lib/chunk-reload.ts.
installChunkReloadGuard();

// Web only: proactively reload an idle tab when a deploy has landed since it
// loaded, so returning to a stale tab and navigating no longer flashes the
// "Something went wrong" page. Native updates via Capgo OTA, not Netlify, so
// there's no index.html to diff — skip it there. See src/lib/chunk-reload.ts.
if (!Capacitor.isNativePlatform()) {
  installStaleBuildWatch();
}

// iOS-only: stop the WKWebView from extending under the Dynamic Island /
// status bar. Without this, our sticky top-0 header lives at touch y=0,
// which iOS reserves for system gestures (notification center swipe,
// Dynamic Island taps). The visible result is exactly what we see in the
// sim today — the header *looks* tappable because contentInset pushes the
// pixels down, but iOS intercepts every tap before WKWebView sees it. With
// overlay=false the WebView frame starts below the status bar and the
// touch zone matches the visible zone.
//
// Errors swallowed because the plugin is iOS/Android-only; calls on web
// throw a "not implemented" exception we don't care about.
if (Capacitor.isNativePlatform()) {
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
  StatusBar.setStyle({ style: Style.Default }).catch(() => {});

  // Self-hosted Capgo OTA. Two things, both native-only:
  //   1. Tag this install's update channel via custom_id. A `--mode staging`
  //      build (mobile-build-ios.sh --staging) reports 'staging'; everything
  //      else reports 'prod'. The ota-check edge function keys off this, so
  //      staging and prod bundles never cross — independent of bundle id.
  //   2. notifyAppReady() tells the plugin this bundle booted successfully. If
  //      it never fires within appReadyTimeout (a white-screened bad bundle),
  //      the plugin auto-rolls back to the last-known-good bundle. This is the
  //      safety net that makes a botched OTA self-heal.
  const otaChannel = import.meta.env.MODE === 'staging' ? 'staging' : 'prod';
  CapacitorUpdater.setCustomId({ customId: otaChannel }).catch(() => {});
  // notifyAppReady() is NOT called here any more — it now runs from the
  // '@/lib/ota-ready' import at the very top of this file. Calling it here
  // meant it sat behind validateEnvironment() (which throws on bad env), the
  // error monitor, the chunk guard and the whole App.tsx import chain; any
  // throw in that prefix stranded the signal and Capgo rolled a healthy
  // bundle back. Do not move it back down here.

  //   3. Observe the OTA lifecycle. Server check-in logs show a subset of
  //      installs download a bundle but never activate it (the auto-swap rolls
  //      back), leaving them stuck on old JS. initOtaUpdates() logs every
  //      @capgo event ([OTA_EVENT] …, visible in the Xcode console) so that
  //      failure is diagnosable on-device, and stages a downloaded bundle for
  //      the OtaUpdateBanner to activate on one tap — deterministically, rather
  //      than waiting for a background→foreground cycle some devices never do.
  initOtaUpdates();

  // Splash: capacitor.config sets launchAutoHide:true with launchShowDuration
  // 1500, so iOS self-dismisses the native splash at ~1.5s no matter what JS
  // does — that timer is the real dismissal mechanism. AppContent additionally
  // calls hide() the moment auth first resolves (see App.tsx), a belt-and-
  // suspenders early-hide that beats the 1.5s timer on a fast warm start.
  // This setTimeout is a further backstop; with launchAutoHide:true it's a
  // redundant no-op (the OS has already hidden the splash), but it's kept so
  // the app still dismisses even if that config ever flips to launchAutoHide:
  // false. hide() is idempotent, so calling it after the auto-hide is safe.
  setTimeout(() => { SplashScreen.hide().catch(() => {}); }, 3000);
}

createRoot(document.getElementById("root")!).render(
  <>
    <App />
    {/* Fixed-position, self-contained (no providers needed); renders nothing
        until an OTA bundle is downloaded and staged. */}
    <OtaUpdateBanner />
  </>
);
